import React from "react";
import styled from "styled-components";

import { CompassParagraph } from "../../components/common/styledComponents";
import { LegendCircle } from "../../components/visuals/styledComponents";
import { darkGreen, greyText } from "../../styles";
import {
  PILLAR_HEATMAP_BANDS,
  PILLAR_HEATMAP_COLORS,
  PillarHeatmapBand,
  SIN_HEATMAP_BANDS,
  SIN_HEATMAP_COLORS,
  SinHeatmapBand,
} from "./constants";

const LegendRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

const PILLAR_BAND_LABEL: Readonly<Record<PillarHeatmapBand, string>> = {
  STRONG: "Strong",
  ADEQUATE: "Adequate",
  MIXED: "Mixed",
  WEAK: "Weak",
  NOT_RATABLE: "Not ratable",
};

const PILLAR_BAND_HINT: Readonly<Record<PillarHeatmapBand, string>> = {
  STRONG: "≥ 0.88",
  ADEQUATE: "0.63–0.88",
  MIXED: "0.38–0.63",
  WEAK: "0.13–0.38",
  NOT_RATABLE: "< 0.13",
};

const SIN_BAND_LABEL: Readonly<Record<SinHeatmapBand, string>> = {
  clean: "Clean",
  warning: "Warning",
  alert: "Alert",
  severe: "Severe",
};

const SIN_BAND_HINT: Readonly<Record<SinHeatmapBand, string>> = {
  clean: "0",
  warning: "≤ 0.25",
  alert: "≤ 0.5",
  severe: "> 0.5",
};

export const HeatmapLegend = ({ variant }: HeatmapLegendProps): JSX.Element => {
  const items =
    variant === "pillar"
      ? PILLAR_HEATMAP_BANDS.map((band) => ({
          key: band,
          color: PILLAR_HEATMAP_COLORS[band],
          label: PILLAR_BAND_LABEL[band],
          hint: PILLAR_BAND_HINT[band],
        }))
      : SIN_HEATMAP_BANDS.map((band) => ({
          key: band,
          color: SIN_HEATMAP_COLORS[band],
          label: SIN_BAND_LABEL[band],
          hint: SIN_BAND_HINT[band],
        }));

  return (
    <LegendRow role="list" aria-label="Heatmap colour legend">
      {items.map((item) => (
        <LegendItem key={item.key} role="listitem">
          <LegendCircle color={item.color} size={10} />
          <CompassParagraph color={darkGreen} fontSize={12} mr={0}>
            {item.label}
          </CompassParagraph>
          <CompassParagraph color={greyText} fontSize={11} ml={4} mr={0}>
            ({item.hint})
          </CompassParagraph>
        </LegendItem>
      ))}
    </LegendRow>
  );
};

type HeatmapLegendProps = { variant: "pillar" } | { variant: "sin" };
